import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';

export default function ApplyPopUp({ isOpen, onClose, electionId, election, dj}) {
  const [profileConfirmed, setProfileConfirmed] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [hasGenerator, setHasGenerator] = useState('no_generator');
  const [hasSpeaker, setHasSpeaker] = useState(null);
  const [hasOtherEquipment, setHasOtherEquipment] = useState(null);
  const [hasTent, setHasTent] = useState(null);
  const [hasTransportation, setHasTransportation] = useState(null);
  const [selectedAvailability, setSelectedAvailability] = useState(null);
  const [interestedAdditional, setInterestedAdditional] = useState(null);
  const [selectedAdditionalDates, setSelectedAdditionalDates] = useState([]);
  const [additionalDropdownOpen, setAdditionalDropdownOpen] = useState(false);
  const [hasHat, setHasHat] = useState(null);
  const [hasTablecloth, setHasTablecloth] = useState(null);
  const [hasTable, setHasTable] = useState(null);
  const [drivingDistance, setDrivingDistance] = useState(null);
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const contentRef = useRef(null);
  const additionalDatesRef = useRef(null);

  const additionalDates = election?.additional_dates || [];

  // DJs who haven't been hired in a prior election won't have DJs-at-the-Polls
  // swag or a Polls-provided generator, so we hide those options for them.
  // const previouslyHired = true;
  const previouslyHired = !!dj?.previously_hired;
  
  // New DJs (not previously hired) skip the profile-confirmation gate entirely;
  // returning DJs must still confirm their profile before the form unlocks.
  const profileGateSatisfied = !previouslyHired || profileConfirmed;
  // Close the additional-dates dropdown when clicking outside of it.
  useEffect(() => {
    if (!additionalDropdownOpen) return;
    const handleClickOutside = (event) => {
      if (additionalDatesRef.current && !additionalDatesRef.current.contains(event.target)) {
        setAdditionalDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [additionalDropdownOpen]);

  const scrollToNextSection = (currentSectionId) => {
    if (!contentRef.current) return;
    
    const sections = contentRef.current.querySelectorAll('.form-section');
    const currentIndex = Array.from(sections).findIndex(section => section.id === currentSectionId);
    
    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      nextSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  const handleStateChange = (value) => {
    setSelectedState(value);
    scrollToNextSection('state-section');
  };

  const handleGeneratorChange = (value) => {
    setHasGenerator(value);
    scrollToNextSection('generator-section');
  };

  const handleSpeakerChange = (value) => {
    setHasSpeaker(value);
    scrollToNextSection('speaker-section');
  };

  const handleEquipmentChange = (value) => {
    setHasOtherEquipment(value);
    scrollToNextSection('equipment-section');
  };

  const handleTentChange = (value) => {
    setHasTent(value);
    scrollToNextSection('tent-section');
  };

  const handleTableChange = (value) => {
    setHasTable(value);
    scrollToNextSection('table-section');
  };

  const handleTransportationChange = (value) => {
    setHasTransportation(value);
    scrollToNextSection('transportation-section');
  };

  const handleAvailabilityChange = (value) => {
    setSelectedAvailability(value);
    scrollToNextSection('shifts-section');
    setError("");
  };

  const handleInterestedAdditionalChange = (value) => {
    setInterestedAdditional(value);
    if (!value) {
      setSelectedAdditionalDates([]);
      setAdditionalDropdownOpen(false);
    }
    setError("");
  };

  const toggleAdditionalDate = (dateRid) => {
    setSelectedAdditionalDates(prev =>
      prev.includes(dateRid)
        ? prev.filter(rid => rid !== dateRid)
        : [...prev, dateRid]
    );
    setError("");
  };

  // Each availability choice maps to the backend set labels (unchanged) it
  // covers. "Available all day" sends all three so the backend still receives
  // the original per-set values.
  const availabilityOptions = [
    {
      id: 'all_day',
      label: 'Available all day (roughly 7am - 7pm)',
      shifts: [
        'Morning Set, 7 am - 11 am',
        'Afternoon Set, 11:30 am - 3:00 pm',
        'Evening Set, 3 pm - 7 pm'
      ]
    },
    {
      id: 'morning',
      label: 'Morning only (roughly 7am-11am)',
      shifts: ['Morning Set, 7 am - 11 am']
    },
    {
      id: 'evening',
      label: 'Evening only (roughly 3pm-7pm)',
      shifts: ['Evening Set, 3 pm - 7 pm']
    }
  ];

  const handleSubmit = async () => {
    if (selectedState === null) {
      setError("Please select a state");
      return;
    }
    if (selectedAvailability === null) {
      setError("Please select your set availability");
      return;
    }
    if (interestedAdditional === null) {
      setError("Please let us know if you're available for additional dates");
      return;
    }
    if (interestedAdditional === true && selectedAdditionalDates.length < 1) {
      setError("Please select at least one additional date");
      return;
    }
    if (previouslyHired && hasHat === null) {
      setError("Please answer if you have the hat");
      return;
    }
    if (previouslyHired && hasTablecloth === null) {
      setError("Please answer if you have the tablecloth");
      return;
    }
    if (hasGenerator === null) {
      setError("Please answer if you have a generator");
      return;
    }
    if (hasSpeaker === null) {
      setError("Please answer if you have a speaker");
      return;
    }
    if (hasOtherEquipment === null) {
      setError("Please answer if you have other DJ equipment");
      return;
    }
    if (hasTent === null) {
      setError("Please answer if you have a tent");
      return;
    }
    if (hasTransportation === null) {
      setError("Please answer if you have transportation");
      return;
    }
    if (hasTable === null) {
      setError("Please answer if you have a table");
      return;
    }
    if (drivingDistance === null) {
      setError("Please select your maximum driving distance");
      return;
    }

    setIsSubmitting(true);
    
    const has_working_gen = hasGenerator;
    const selectedAvailabilityOption = availabilityOptions.find(option => option.id === selectedAvailability);
    const availablity = selectedAvailabilityOption ? selectedAvailabilityOption.shifts.join("; ") : "";
    const qb_record = {
      related_dj: dj.rid,
      related_election: electionId,
      states_dj_applied: selectedState,
      has_working_gen: has_working_gen,
      shifts_availability: availablity,
      has_hat: previouslyHired ? hasHat : false,
      has_tablecloth: previouslyHired ? hasTablecloth : false,
      max_driving_distance: drivingDistance,
      has_tent: hasTent,
      has_transportation: hasTransportation,
      has_speaker: hasSpeaker,
      has_dj_equipment: hasOtherEquipment,
      has_table: hasTable,
      additional_date_rids: interestedAdditional ? selectedAdditionalDates : []
    }
    try {
      await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/createDJElection`, qb_record);
      console.log('Application submitted successfully');
      onClose({ success: true });
    } catch (error) {
      console.error('Error submitting application:', error);
      onClose({ success: false, error: 'Failed to submit application' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  // The generator question is rendered in different positions depending on
  // whether the DJ was previously hired, so it lives in one place here and is
  // dropped into the correct spot in the form below.
  const generatorSection = (
    <div id="generator-section" className="space-y-4 form-section">
      <label className="block text-lg font-medium text-gray-900">
        Generator <span className="text-red-500">*</span>
      </label>
      <label className="block text-sm font-medium text-gray-900">
        Do you have a working generator that you can use to power your equipment?
      </label>

      <div className="space-y-3">
        {previouslyHired && (
          <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
            <input
              type="radio"
              name="generator"
              checked={hasGenerator === 'Yes, I have a generator provided by DJs at the Polls (returning DJs only)'}
              onChange={() => handleGeneratorChange('Yes, I have a generator provided by DJs at the Polls (returning DJs only)')}
              className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
            />
            <span className="ml-3 text-sm font-medium text-gray-700">Yes, I have a generator provided by DJs at the Polls (returning DJs only)</span>
          </label>
        )}
        <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="radio"
            name="generator"
            checked={hasGenerator === 'Yes, I have my own generator, but not one provided by DJs at the Polls'}
            onChange={() => handleGeneratorChange('Yes, I have my own generator, but not one provided by DJs at the Polls')}
            className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
          />
          <span className="ml-3 text-sm font-medium text-gray-700">{previouslyHired ? 'Yes, I have my own generator, but not one provided by DJs at the Polls' : 'Yes, I have my own generator'}</span>
        </label>

        <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
          <input
            type="radio"
            name="generator"
            checked={hasGenerator === 'No, I don\'t have a generator'}
            onChange={() => handleGeneratorChange('No, I don\'t have a generator')}
            className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
          />
          <span className="ml-3 text-sm font-medium text-gray-700">No, I don't have a generator</span>
        </label>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full mx-auto relative my-8 sm:my-0">
        <div 
          ref={contentRef}
          className="max-h-[85vh] overflow-y-auto [&::-webkit-scrollbar]:hidden"
        >
          {/* Header */}
          <div className="sticky top-0 z-20 bg-white p-4 border-b border-gray-100 rounded-t-2xl">
            <img
              src="/DJLogo-Horiz.png"
              alt="DJs at the Polls"
              className="h-16 w-auto mb-2"
            />
            <h2 className="text-xl font-semibold text-gray-900">
              Information Required for This Election
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Please provide the following information to complete your application.
            </p>
            <button
              onClick={() => onClose({ success: false })}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="p-6 space-y-6">
            {/* Profile confirmation — gates the rest of the form for returning DJs only.
                New DJs skip it. Not saved to QuickBase. */}
            {previouslyHired && (
            <div className="space-y-3 form-section">
              <label className="flex items-start p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={profileConfirmed}
                  onChange={() => setProfileConfirmed(prev => !prev)}
                  className="h-4 w-4 mt-0.5 text-primary border-gray-300 rounded focus:ring-primary"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">
                  I confirm the information in my profile is correct. To verify this information, please close this box, then click on PROFILE to review and make any updates. Select Dashboard, then Apply Now to return to the application
                </span>
              </label>
            </div>
            )}

            {profileGateSatisfied && (
              <>
            {/* State Selection Question */}
            <div id="state-section" className="space-y-4 form-section">
              <label className="block text-lg font-medium text-gray-900">
                Select State <span className="text-red-500">*</span>
              </label>
              <label className="block text-sm font-medium text-gray-900">
                Which state are you applying for?
              </label>
              
              <div className="space-y-3">
                <select
                  name="state"
                  value={selectedState ?? ''}
                  onChange={(e) => handleStateChange(e.target.value)}
                  className="block w-full p-3 border border-gray-300 rounded-lg bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors cursor-pointer"
                >
                  <option value="" disabled>
                    Choose a state...
                  </option>
                  {election?.all_states?.map((state) => (
                    <option key={state} value={state}>
                      {state.trim()}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Generator Question — returning DJs see it here (before Speaker);
                new DJs see it after the Other DJ Equipment question (see below). */}
            {previouslyHired && generatorSection}

            {/* Speaker Question */}
            <div id="speaker-section" className="space-y-4 form-section">
              <label className="block text-lg font-medium text-gray-900">
                Speaker Equipment <span className="text-red-500">*</span>
              </label>
              <label className="block text-sm font-medium text-gray-900">
                Do you have a speaker that you can use for your sets?
              </label>
             
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="speaker"
                    checked={hasSpeaker === true}
                    onChange={() => handleSpeakerChange(true)}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">Yes, I have a speaker</span>
                </label>
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="speaker"
                    checked={hasSpeaker === false}
                    onChange={() => handleSpeakerChange(false)}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">No, I don't have a speaker</span>
                </label>
              </div>
            </div>

            {/* Other DJ Equipment Question */}
            <div id="equipment-section" className="space-y-4 form-section">
              <label className="block text-lg font-medium text-gray-900">
                Other DJ Equipment <span className="text-red-500">*</span>
              </label>
              <label className="block text-sm font-medium text-gray-900">
                Do you have other necessary DJ equipment that you need (e.g., mixer, laptop, controller)?
              </label>
              <p className="text-sm text-gray-500 -mt-2">
                You'll need your own DJ equipment to perform your sets.
              </p>
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="other_equipment"
                    checked={hasOtherEquipment === true}
                    onChange={() => handleEquipmentChange(true)}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">Yes, I have all necessary DJ equipment</span>
                </label>
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="other_equipment"
                    checked={hasOtherEquipment === false}
                    onChange={() => handleEquipmentChange(false)}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">No, I'm missing some equipment</span>
                </label>
              </div>
            </div>

            {/* Generator Question — new DJs see it here, after Other DJ Equipment. */}
            {!previouslyHired && generatorSection}

            {/* Other Supplies */}
            <div className="space-y-4">
              <label className="block text-lg font-medium text-gray-900">
                Other Supplies <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-500 -mt-2">
                Please indicate other supplies you have for DJ performances.
              </p>
              
              {/* Tent Question */}
              <div id="tent-section" className="space-y-4 form-section">
                <label className="block text-sm font-medium text-gray-900">
                  Do you have a tent that you can use to protect your equipment from weather if needed?
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="tent"
                      checked={hasTent === true}
                      onChange={() => handleTentChange(true)}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">Yes, I have a tent I can use</span>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="tent"
                      checked={hasTent === false}
                      onChange={() => handleTentChange(false)}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">No, I don't have a tent</span>
                  </label>
                </div>
              </div>

              {/* Table Question */}
              <div id="table-section" className="space-y-4 form-section">
                <label className="block text-sm font-medium text-gray-900">
                  Do you have a portable table you can use for your equipment?
                </label>
                <div className="space-y-3">
                  <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="table"
                      checked={hasTable === true}
                      onChange={() => handleTableChange(true)}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">Yes, I have a portable table</span>
                  </label>
                  <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="table"
                      checked={hasTable === false}
                      onChange={() => handleTableChange(false)}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">No, I don't have a table</span>
                  </label>
                </div>
              </div>
            </div>

            {/* Transportation Question */}
            <div id="transportation-section" className="space-y-4 form-section">
              <label className="block text-lg font-medium text-gray-900">
                Transportation <span className="text-red-500">*</span>
              </label>
              <label className="block text-sm font-medium text-gray-900">
                Do you have reliable transportation to get to the polls?
              </label>
              <p className="text-sm text-gray-500 -mt-2">
                You'll need to transport yourself and your equipment to your assigned polling location.
              </p>
              <div className="space-y-3">
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="transportation"
                    checked={hasTransportation === true}
                    onChange={() => handleTransportationChange(true)}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">Yes, I have reliable transportation</span>
                </label>
                <label className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                  <input
                    type="radio"
                    name="transportation"
                    checked={hasTransportation === false}
                    onChange={() => handleTransportationChange(false)}
                    className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">No, I don't have reliable transportation</span>
                </label>
              </div>
            </div>

            {/* Set Preferences */}
            <div id="shifts-section" className="space-y-4 form-section">
              <div className="space-y-4">
                <label className="block text-lg font-medium text-gray-900">
                  Preferred Sets <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-900">
                  We pay $500 per set (provided you have your own equipment).
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900">
                    Please select your available time slots. Note: all selections are requests. IF YOU ARE HIRED, WE DO NOT GUARANTEE YOU WILL RECEIVE MORE THAN ONE SET.
                  </p>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <span className={selectedAvailability !== null ? "text-green-600" : "text-amber-600"}>
                    {selectedAvailability !== null ? "Availability selected" : `Select your availability on ${election?.election_date}`}
                  </span>
                </div>
              </div>
              <div className="space-y-3">
                {availabilityOptions.map((option) => (
                  <label key={option.id} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="set_availability"
                      checked={selectedAvailability === option.id}
                      onChange={() => handleAvailabilityChange(option.id)}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">{option.label}</span>
                  </label>
                ))}
              </div>

              {/* Additional Dates Availability */}
              <div className="space-y-3 pt-2">
                <label className="block text-sm font-medium text-gray-900">
                  Are you interested/available to DJ the weekend prior to Election Day if needed?
                </label>
                <div className="flex gap-6">
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="interested_additional"
                      checked={interestedAdditional === true}
                      onChange={() => handleInterestedAdditionalChange(true)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">Yes</span>
                  </label>
                  <label className="inline-flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="interested_additional"
                      checked={interestedAdditional === false}
                      onChange={() => handleInterestedAdditionalChange(false)}
                      className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                    />
                    <span className="ml-2 text-sm text-gray-900">No</span>
                  </label>
                </div>

                {interestedAdditional === true && (
                  <div ref={additionalDatesRef} className="relative">
                    <label className="block text-sm font-medium text-gray-900 mb-1">
                      Which additional dates are you available for? <span className="text-red-500">*</span>
                    </label>
                    <button
                      type="button"
                      onClick={() => setAdditionalDropdownOpen((open) => !open)}
                      className="flex w-full items-center justify-between p-3 border border-gray-300 rounded-lg bg-white text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors"
                    >
                      <span className={selectedAdditionalDates.length === 0 ? "text-gray-400" : "text-gray-700"}>
                        {selectedAdditionalDates.length === 0
                          ? "Choose dates..."
                          : `${selectedAdditionalDates.length} date${selectedAdditionalDates.length === 1 ? "" : "s"} selected`}
                      </span>
                      <svg
                        className={`w-5 h-5 text-gray-400 transition-transform ${additionalDropdownOpen ? "rotate-180" : ""}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    {additionalDropdownOpen && (
                      <div className="absolute z-10 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                        {additionalDates.length === 0 ? (
                          <p className="p-3 text-sm text-gray-500">No additional dates are available right now.</p>
                        ) : (
                          additionalDates.map((additionalDate) => (
                            <label
                              key={additionalDate.rid}
                              className="flex items-center p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                            >
                              <input
                                type="checkbox"
                                checked={selectedAdditionalDates.includes(additionalDate.rid)}
                                onChange={() => toggleAdditionalDate(additionalDate.rid)}
                                className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                              />
                              <span className="ml-3 text-sm font-medium text-gray-700">{additionalDate.label}</span>
                            </label>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <p className="text-sm text-red-600 mt-2">{error}</p>
              )}
            </div>

            {/* Driving Distance Question */}
            <div className="space-y-4">
              <label className="block text-lg font-medium text-gray-900">
                Driving Distance <span className="text-red-500">*</span>
              </label>
              <p className="text-sm text-gray-500 mt-1">
              Whenever possible, we assign DJs close to their homes. We may need some DJs to travel to areas where we don't have coverage. DJs will be compensated for total travel over 50 miles.
              </p>
              <label className="block text-sm font-medium text-gray-900">
              How far are you willing to drive one way?
              </label>
              <div className="space-y-3">
                {['Up to 25','Up to 50', 'Up to 75', '75+'].map((distance) => (
                  <label key={distance} className="flex items-center p-3 border rounded-lg hover:bg-gray-50 cursor-pointer transition-colors">
                    <input
                      type="radio"
                      name="driving_distance"
                      value={distance}
                      checked={drivingDistance === distance}
                      onChange={() => setDrivingDistance(distance)}
                      className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-3 text-sm font-medium text-gray-700">{distance} miles</span>
                  </label>
                ))}
              </div>
             
            </div>

            {/* Swag Questions — only relevant for DJs hired in a prior election */}
            {previouslyHired && (
            <div className="space-y-6 mt-6">
              <div className="space-y-4">
                <label className="block text-lg font-medium text-gray-900">
                  Swag Questions <span className="text-red-500">*</span>
                </label>
                <p className="text-sm text-gray-500">
                  If you worked in a previous election, please answer the following questions. If you didn't, just answer No for both.
                </p>
                {/* Hat Question */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Do you still have the "DJs at the Polls" hat? <span className="text-red-500">*</span>
                  </label>
                  <div className="space-x-6">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="has_hat"
                        value="yes"
                        checked={hasHat === true}
                        onChange={() => setHasHat(true)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-900">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="has_hat"
                        value="no"
                        checked={hasHat === false}
                        onChange={() => setHasHat(false)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-900">No</span>
                    </label>
                  </div>
                </div>

                {/* Tablecloth Question */}
                <div>
                  <label className="block text-sm font-medium text-gray-900 mb-2">
                    Do you still have the "DJs at the Polls" tablecloth? <span className="text-red-500">*</span>
                  </label>
                  <div className="space-x-6">
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="has_tablecloth"
                        value="yes"
                        checked={hasTablecloth === true}
                        onChange={() => setHasTablecloth(true)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-900">Yes</span>
                    </label>
                    <label className="inline-flex items-center">
                      <input
                        type="radio"
                        name="has_tablecloth"
                        value="no"
                        checked={hasTablecloth === false}
                        onChange={() => setHasTablecloth(false)}
                        className="h-4 w-4 text-primary focus:ring-primary border-gray-300"
                      />
                      <span className="ml-2 text-sm text-gray-900">No</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            )}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-white p-6 border-t border-gray-100 flex justify-end space-x-3 rounded-b-2xl shadow-lg">
            <button
              onClick={() => onClose({ success: false })}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!profileGateSatisfied || selectedState === null || selectedAvailability === null || interestedAdditional === null || (interestedAdditional === true && selectedAdditionalDates.length < 1) || (previouslyHired && (hasHat === null || hasTablecloth === null)) || hasGenerator === null || drivingDistance === null || hasTent === null || hasTransportation === null || hasSpeaker === null || hasOtherEquipment === null || hasTable === null || isSubmitting}
              className={`px-4 py-2 text-sm font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary ${
                !profileGateSatisfied || selectedState === null || selectedAvailability === null || interestedAdditional === null || (interestedAdditional === true && selectedAdditionalDates.length < 1) || (previouslyHired && (hasHat === null || hasTablecloth === null)) || hasGenerator === null || drivingDistance === null || hasTent === null || hasTransportation === null || hasSpeaker === null || hasOtherEquipment === null || hasTable === null || isSubmitting
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-primary hover:bg-primary-dark'
              }`}
            >
              {isSubmitting ? 'Submitting...' : 'Apply'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
